/**
 * useInterviewSession Hook
 *
 * Manages interview session lifecycle including user profile, session creation,
 * call registration, and remaining interview tracking.
 *
 * @module hooks/useInterviewSession
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '@/lib/api-client';
import { track as breadcrumb } from '@/lib/telemetry/breadcrumb';
import type { SessionData, Theme, UserProfile } from '@/types/interview';
import { TimeoutError, withTimeout } from '@/utils/api-client';
import { apiClient } from '@/utils/api-error-handler';
import { logger } from '@/utils/logger';
import { createClient } from '@/utils/supabase/client';

// LUM-81: registerCallId retry policy. The cost asymmetry is extreme —
// a missed registration can zombie a 40-minute interview — so we retry
// every failure class rather than special-casing retryable ones.
const REGISTER_CALL_MAX_ATTEMPTS = 3;
const REGISTER_CALL_RETRY_DELAYS_MS = [1000, 3000];

export interface UseInterviewSessionOptions {
  /** Callback when authentication fails */
  onAuthError?: (message: string) => void;
}

export interface UseInterviewSessionReturn {
  /** Whether data is loading */
  loading: boolean;
  /** User profile */
  profile: UserProfile | null;
  /** Remaining interviews (-1 = unlimited, null = unknown) */
  remainingInterviews: number | null;
  /** Whether user can start an interview */
  canStartInterview: boolean;
  /** Current session ID */
  sessionId: string | null;
  /** Current session data */
  sessionData: SessionData | null;
  /** Themes for the interview */
  themes: Theme[];
  /** Create a new interview session */
  createSession: () => Promise<SessionData | null>;
  /** Register call ID with the session */
  registerCallId: (callId: string) => Promise<boolean>;
  /** Update session with call end data */
  updateSessionOnCallEnd: (callId: string) => Promise<SessionData | null>;
  /** Reset session for new interview */
  resetSession: () => void;
  /** Decrement remaining interviews (optimistic update) */
  decrementRemainingInterviews: () => void;
  /** Refresh remaining interviews from server */
  refreshRemainingInterviews: () => Promise<void>;
}

/**
 * Hook for managing interview session lifecycle
 *
 * Provides:
 * - User profile loading and authentication
 * - Interview limit tracking
 * - Session creation and management
 * - Call ID registration
 * - Theme fetching
 */
export function useInterviewSession(
  options: UseInterviewSessionOptions = {},
): UseInterviewSessionReturn {
  const { onAuthError } = options;

  // User state
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [remainingInterviews, setRemainingInterviews] = useState<number | null>(
    null,
  );

  // Session state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);

  // Theme state
  const [themes, setThemes] = useState<Theme[]>([]);

  // Refs
  const sessionIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  // Store callbacks in refs to avoid re-triggering effects when they change
  const onAuthErrorRef = useRef(onAuthError);

  // Keep refs updated with latest callbacks
  useEffect(() => {
    onAuthErrorRef.current = onAuthError;
  }, [onAuthError]);

  // Track mounted state for cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Computed value
  const canStartInterview =
    remainingInterviews === null ||
    remainingInterviews === -1 ||
    remainingInterviews > 0;

  // Fetch user profile + interview-limit count: parallel queries + RLS
  // error branches + setter dispatch. Extraction fragments the
  // closure for marginal readability gain.
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: linear data-fetch effect
  const fetchUserData = useCallback(async () => {
    const supabase = createClient();

    try {
      // Get current user
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        onAuthErrorRef.current?.('Authentication required');
        setLoading(false);
        return;
      }

      // Get user profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError || !profileData) {
        // Create profile if it doesn't exist
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            email: user.email,
            full_name: user.user_metadata?.full_name || user.email,
            organization: user.user_metadata?.organization,
            interview_limit: 1,
          })
          .select()
          .single();

        if (insertError || !newProfile) {
          logger.error('Failed to create profile', { error: insertError });
          onAuthErrorRef.current?.('Failed to create user profile');
          setLoading(false);
          return;
        }

        setProfile(newProfile);
      } else {
        setProfile(profileData);
      }

      // Get remaining interviews
      const { data: remaining, error: remainingError } = await supabase.rpc(
        'get_remaining_interviews',
        { p_user_id: user.id },
      );

      if (!remainingError) {
        setRemainingInterviews(remaining);
      } else {
        logger.warn('Error fetching remaining interviews', {
          error: remainingError,
        });
      }
    } catch (err) {
      logger.error('Error fetching user data', {
        error: err instanceof Error ? err.message : String(err),
      });
      onAuthErrorRef.current?.('Failed to load user information');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch themes
  const fetchThemes = useCallback(async () => {
    try {
      const response = await apiFetch('/api/interview/themes');
      if (response.ok) {
        const result: {
          success?: boolean;
          data?: { themes?: Theme[] };
        } = await response.json();
        if (result.success && result.data?.themes) {
          setThemes(result.data.themes);
        }
      }
    } catch (error) {
      logger.error('Failed to fetch themes', { error });
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    void fetchUserData();
    void fetchThemes();
  }, [fetchUserData, fetchThemes]);

  // Refresh remaining interviews on window focus
  useEffect(() => {
    const handleFocus = () => {
      if (profile) {
        const supabase = createClient();
        supabase
          .rpc('get_remaining_interviews', { p_user_id: profile.id })
          .then(({ data, error }) => {
            if (!error && data !== null) {
              setRemainingInterviews(data);
            }
          });
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [profile]);

  // Create a new interview session: insert + timeout/error mapping.
  const createSession = useCallback(async (): Promise<SessionData | null> => {
    const supabase = createClient();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('User not authenticated');
      }

      // The call itself needs no assistant id (the worker owns the
      // realtime session config). The profile's value, if any, is only
      // copied onto the session row for dd-admin's wave queries, which
      // filter on interview_sessions.vapi_assistant_id.
      const { data: profileData } = await supabase
        .from('profiles')
        .select('vapi_assistant_id')
        .eq('id', user.id)
        .single();

      // Create session with timeout
      const sessionPromise = supabase
        .from('interview_sessions')
        .insert({
          user_id: user.id,
          status: 'pending',
          interview_type: 'discovery',
          vapi_assistant_id: profileData?.vapi_assistant_id ?? null,
          themes: [],
          allow_repeat: false,
        })
        .select()
        .single();

      const { data: session, error: sessionError } = await withTimeout(
        sessionPromise,
        30000,
      );

      if (sessionError || !session) {
        throw new Error('Failed to create interview session');
      }

      logger.debug('Created session', { sessionId: session.id });
      // LUM-81 binding-chain breadcrumb #1: the DB row now exists with
      // status='pending'. `interview_session_id` (not `session_id`,
      // which the telemetry route reserves) is the correlation key for
      // the rest of the chain.
      void breadcrumb('interview_session_created', {
        interview_session_id: session.id,
      });

      setSessionId(session.id);
      sessionIdRef.current = session.id;
      setSessionData(session);

      return session;
    } catch (err) {
      logger.error('Failed to create session', {
        error: err instanceof Error ? err.message : String(err),
      });

      if (err instanceof TimeoutError) {
        throw new Error(
          'Connection timed out. Please check your internet connection.',
        );
      }

      throw err;
    }
  }, []);

  // Register call ID with the session.
  //
  // LUM-81: this write binds the relay call id to the session row the
  // end-of-call poll reads back. If it never lands, the session's
  // history entry can lag, so failures retry with backoff, every
  // attempt leaves a breadcrumb, and the final outcome is returned to
  // the caller so it can surface a user-visible warning instead of
  // swallowing it.
  const registerCallId = useCallback(
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: linear retry loop — attempt/success/failure legs read top-to-bottom
    async (callId: string): Promise<boolean> => {
      const currentSessionId = sessionIdRef.current;

      if (!currentSessionId) {
        logger.warn('Cannot register call ID - no session');
        return false;
      }

      let lastErrorCode = 'unknown';

      for (
        let attempt = 1;
        attempt <= REGISTER_CALL_MAX_ATTEMPTS;
        attempt += 1
      ) {
        void breadcrumb('register_call_id_attempt', {
          interview_session_id: currentSessionId,
          call_id: callId,
          attempt_n: attempt,
        });

        try {
          await apiClient('/api/interview/register-call', {
            method: 'POST',
            body: JSON.stringify({
              sessionId: currentSessionId,
              callId: callId,
            }),
          });

          logger.info('Call ID registered successfully', {
            sessionId: currentSessionId,
            callId: callId,
            attempt,
          });
          void breadcrumb('register_call_id_success', {
            interview_session_id: currentSessionId,
            call_id: callId,
            attempt_n: attempt,
          });

          return true;
        } catch (err) {
          // Ship the InterviewError code enum, never the message —
          // messages can embed URLs or response snippets.
          lastErrorCode =
            err && typeof err === 'object' && 'code' in err
              ? String((err as { code: unknown }).code)
              : 'unknown';
          logger.error('Error registering call ID', {
            error: err instanceof Error ? err.message : String(err),
            sessionId: currentSessionId,
            callId: callId,
            attempt,
          });

          if (attempt < REGISTER_CALL_MAX_ATTEMPTS) {
            await new Promise(resolve =>
              setTimeout(resolve, REGISTER_CALL_RETRY_DELAYS_MS[attempt - 1]),
            );
          }
        }
      }

      void breadcrumb('register_call_id_failed', {
        interview_session_id: currentSessionId,
        call_id: callId,
        attempt_n: REGISTER_CALL_MAX_ATTEMPTS,
        reason_category: lastErrorCode,
      });

      return false;
    },
    [],
  );

  // Update session on call end (poll for final data): retry loop +
  // freshness check + state-machine on poll responses.
  const updateSessionOnCallEnd = useCallback(
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: polling state machine
    async (callId: string): Promise<SessionData | null> => {
      const supabase = createClient();
      const currentSessionId = sessionIdRef.current;

      if (!callId && !currentSessionId) {
        logger.warn('Cannot update session - no call ID or session ID');
        return null;
      }

      // Poll until finalize + the summary write land the final status
      const maxAttempts = 12;
      const delayMs = 300;
      let attempt = 0;
      let finalSession: SessionData | null = null;

      while (attempt < maxAttempts && isMountedRef.current) {
        let query = supabase
          .from('interview_sessions')
          .select('id, themes, duration, status, end_reason, summary');

        if (callId) {
          query = query.eq('vapi_call_id', callId);
        } else if (currentSessionId) {
          query = query.eq('id', currentSessionId);
        }

        const { data: sessionData } = await query.single();

        if (sessionData) {
          finalSession = sessionData;

          // Stop polling once we have terminal status and summary
          const terminal =
            sessionData.status === 'completed' ||
            sessionData.status === 'failed';
          const summaryReady = !!sessionData.summary;

          logger.debug('Call-end polling tick', {
            attempt,
            terminal,
            status: sessionData.status,
            summaryReady,
          });

          if (terminal && (summaryReady || attempt >= maxAttempts - 1)) {
            break;
          }
        }

        await new Promise(resolve => setTimeout(resolve, delayMs));
        attempt += 1;
      }

      // Only update state if still mounted
      if (finalSession && isMountedRef.current) {
        setSessionData(finalSession);
      }

      return finalSession;
    },
    [],
  );

  // Reset session for new interview
  const resetSession = useCallback(() => {
    setSessionId(null);
    sessionIdRef.current = null;
    setSessionData(null);
  }, []);

  // Decrement remaining interviews (optimistic update)
  const decrementRemainingInterviews = useCallback(() => {
    setRemainingInterviews(prev => {
      if (prev === null || prev === -1) return prev;
      return Math.max(0, prev - 1);
    });
  }, []);

  // Refresh remaining interviews from server
  const refreshRemainingInterviews = useCallback(async () => {
    if (!profile) return;

    const supabase = createClient();
    const { data, error } = await supabase.rpc('get_remaining_interviews', {
      p_user_id: profile.id,
    });

    if (!error && data !== null) {
      setRemainingInterviews(data);
    }
  }, [profile]);

  return {
    loading,
    profile,
    remainingInterviews,
    canStartInterview,
    sessionId,
    sessionData,
    themes,
    createSession,
    registerCallId,
    updateSessionOnCallEnd,
    resetSession,
    decrementRemainingInterviews,
    refreshRemainingInterviews,
  };
}

export default useInterviewSession;
